// frontend/components/sidebar/SidebarSection.tsx

"use client";

import { useState } from "react";
import { Plus, ChevronDown, ChevronRight } from "lucide-react";
import { useSidebarContext } from "../../contexts/SidebarContext";

interface SidebarSectionProps {
  title: string;
  icon: React.ReactNode;
  dragOverId?: string | null;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onAddClick?: () => void;
  isEmpty: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}

// 대분류 단위의 사이드바 섹션 영역 렌더링 및 드롭 이벤트 처리
export default function SidebarSection({
  title, icon, dragOverId, onDragOver, onDragLeave, onDrop, onAddClick, isEmpty, emptyMessage, children
}: SidebarSectionProps) {
  const { colorTheme } = useSidebarContext();
  const isNeutral = colorTheme === "neutral";
  const [isExpanded, setIsExpanded] = useState(true);

  let myTargetId = null;
  if (title.includes("팀 스페이스")) myTargetId = "TEAM_ROOT";
  if (title.includes("개인 페이지")) myTargetId = "PERSONAL_ROOT";
  const isTargetDragOver = dragOverId === myTargetId;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragOver) onDragOver(e);
  };

  const toggleSection = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div 
      className={`space-y-0.5 p-1 rounded-lg transition-all duration-200 ${isTargetDragOver ? 'bg-[#2EAADC]/15 ring-1 ring-[#2EAADC]/50' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div 
        className={`group flex items-center justify-between text-[11px] font-bold px-2 tracking-wide mb-1 cursor-pointer opacity-80 hover:opacity-100 transition-opacity ${isNeutral ? 'text-gray-500 dark:text-gray-400' : 'text-white/60'}`}
        onClick={toggleSection}
      >
        {/* 좌측 영역: 아이콘 + 제목 + 꺾쇠 */}
        <div className="flex items-center space-x-1.5 uppercase">
          {icon} 
          <span>{title}</span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {isExpanded ? <ChevronDown size={13} strokeWidth={2.5} /> : <ChevronRight size={13} strokeWidth={2.5} />}
          </div>
        </div>
        
        {/* 우측 영역: 추가(+) 버튼 */}
        <div className="flex items-center">
          {onAddClick && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onAddClick();
              }} 
              className={`p-1 rounded transition-colors opacity-0 group-hover:opacity-100 ${isNeutral ? 'hover:bg-gray-200 dark:hover:bg-zinc-800 text-gray-600 dark:text-gray-300' : 'hover:bg-white/20 text-white'}`}
              title="페이지 추가"
            >
              <Plus size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="space-y-0">
          {isEmpty ? (
            <p className={`text-[11px] italic p-1.5 pl-3 pointer-events-none ${isNeutral ? 'text-gray-400 dark:text-gray-600' : 'text-white/40'}`}>
              {emptyMessage}
            </p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}